/* Load once globally in theme.liquid. Uses session-only shopping signals. */
(() => {
  if (window.herzensIntentRecommendations) return;
  window.herzensIntentRecommendations = true;
  const key = 'herzens-intent-v1';
  const selector = '[data-intent-recommendations]';
  const root = window.Shopify?.routes?.root || '/';
  const ttl = 30 * 60 * 1000;
  let state = { views: [], search: null };
  try {
    const saved = JSON.parse(sessionStorage.getItem(key));
    if (saved && Array.isArray(saved.views)) state = saved;
  } catch (_) {}
  state.views = state.views.filter(v => v && /^\d+$/.test(String(v.id)) && Date.now() - v.at < ttl).slice(0, 6);
  if (!state.search || Date.now() - state.search.at >= ttl) state.search = null;
  function save() { try { sessionStorage.setItem(key, JSON.stringify(state)); } catch (_) {} }
  function recordSearch(value) {
    const q = String(value || '').trim().slice(0, 150);
    if (!q) return;
    state.search = { q, at: Date.now() };
    save();
  }
  const page = new URL(location.href);
  if (/\/search\/?$/.test(page.pathname)) recordSearch(page.searchParams.get('q'));
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, location.href);
    if (action.origin === location.origin && /\/search\/?$/.test(action.pathname)) {
      recordSearch(new FormData(form).get('q'));
    }
  }, true);
  // Capture a committed predictive-search click without storing every keystroke.
  document.addEventListener('click', event => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    const form = link?.closest('form');
    if (!form) return;
    const action = new URL(form.action, location.href);
    const destination = new URL(link.href, location.href);
    if (action.origin === location.origin && /\/search\/?$/.test(action.pathname) && destination.origin === location.origin && destination.pathname.includes('/products/')) {
      recordSearch(new FormData(form).get('q'));
    }
  }, true);
  const cache = new Map();
  function json(path, params = {}) {
    const url = new URL(root + path, location.origin);
    Object.entries(params).forEach(([k,v]) => url.searchParams.set(k,v));
    const key = url.href;
    if (!cache.has(key)) {
      cache.set(key, (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`${path}: HTTP ${response.status}`);
          return await response.json();
        } finally { clearTimeout(timer); }
      })());
    }
    return cache.get(key);
  }
  async function safely(request) {
    try { return await request(); }
    catch (error) { console.warn('[Intent recommendations]', error); return []; }
  }
  const related = id => safely(async () => (await json('recommendations/products.json', {
    product_id: id, limit: 10, intent: 'related'
  })).products || []);
  function normalize(product, predictive = false) {
    return {
      id: String(product.id), title: product.title, url: product.url,
      image: typeof product.image === 'string' ? product.image :
        (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url),
      handle: product.handle,
      productType: String(product.product_type || product.type || '').trim().toLocaleLowerCase(),
      available: product.available
    };
  }
  function safeUrl(value, image = false) {
    if (!value) return null;
    try {
      const url = new URL(value, location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      if (!image && url.origin !== location.origin) return null;
      return url.href;
    } catch (_) { return null; }
  }
  async function load(container) {
    if (container.dataset.loading || container.dataset.loaded) return;
    const current = container.dataset.productId || '';
    const isCartPage = container.dataset.pageType === 'cart';
    if (!isCartPage && !/^\d+$/.test(current)) return;
    container.dataset.loading = 'true';
    const previous = state.views.filter(v => String(v.id) !== current).slice(0, 3);
    if (current) {
      state.views = [{ id: current, at: Date.now() }, ...state.views.filter(v => String(v.id) !== current)].slice(0, 6);
      save();
    }
    const limit = Math.min(8, Math.max(4, Number(container.dataset.limit) || 4));
    const excluded = new Set(current ? [current] : []);
    let cartSeeds = [];
    const selected = new Map();
    const deferred = new Map();
    const typeCounts = new Map();
    const maxPerType = 2;
    function add(products, predictive = false) {
      for (const raw of products) {
        if (selected.size >= limit) break;
        const p = normalize(raw, predictive);
        if (excluded.has(p.id) || p.available !== true || !safeUrl(p.image, true) || selected.has(p.id) || !/^[a-zA-Z0-9_-]+$/.test(p.handle || '')) continue;
        // Products without a type share one bucket; we cannot infer their category.
        const type = p.productType || '__unclassified__';
        if ((typeCounts.get(type) || 0) >= maxPerType) {
          if (!deferred.has(p.id)) deferred.set(p.id, p);
          continue;
        }
        selected.set(p.id, p);
        typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
      }
    }
    function fillRemaining() {
      // Relax the cap only after every recommendation source has had a turn.
      for (const p of deferred.values()) {
        if (selected.size >= limit) break;
        if (!selected.has(p.id)) selected.set(p.id, p);
      }
    }
    try {
      if (isCartPage) {
        const cart = await json('cart.js');
        cart.items.forEach(item => excluded.add(String(item.product_id)));
        cartSeeds = [...new Set(cart.items.map(item => String(item.product_id)))].slice(0, 3);
      }
      if (state.search && Date.now() - state.search.at < ttl) {
        const matches = await safely(async () => (await json('search/suggest.json', {
          q: state.search.q, 'resources[type]': 'product',
          'resources[limit]': 10, 'resources[options][unavailable_products]': 'hide'
        })).resources?.results?.products || []);
        add(matches, true);
      }
      if (selected.size < limit && previous.length) {
        const groups = await Promise.all(previous.map(v => related(v.id)));
        // Interleave sources so one viewed product does not fill every slot.
        for (let i = 0; i < 10 && selected.size < limit; i++) {
          for (const group of groups) {
            if (group[i]) add([group[i]]);
            if (selected.size >= limit) break;
          }
        }
      }
      if (selected.size < limit) {
        const seeds = isCartPage ? cartSeeds : [current];
        const groups = await Promise.all(seeds.map(id => related(id)));
        for (let i = 0; i < 10 && selected.size < limit; i++) {
          for (const group of groups) {
            if (group[i]) add([group[i]]);
            if (selected.size >= limit) break;
          }
        }
      }
      fillRemaining();
      if (!container.isConnected) return;
      const wrapper = container.querySelector('.swiper-wrapper');
      if (!wrapper) throw new Error('Sahara slider wrapper missing');
      // Use exactly the native card view used by Recently Viewed.
      const cards = await Promise.all([...selected.values()].map(async product => {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 8000);
          let html;
          try {
            const url = new URL(root + 'products/' + encodeURIComponent(product.handle), location.origin);
            url.searchParams.set('view', 'card');
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) throw new Error(`Card: HTTP ${response.status}`);
            html = await response.text();
          } finally { clearTimeout(timer); }
          const slide = document.createElement('div');
          slide.className = 'swiper-slide card-product-slider__slide';
          slide.innerHTML = html;
          if (!slide.querySelector('card-product') || !slide.querySelector('.card-product')) return null;
          return slide;
        } catch (error) {
          console.warn('[Intent recommendations card]', error);
          return null;
        }
      }));
      if (!container.isConnected) return;
      // Native card views may repeat form IDs. Keep IDs and references unique
      // when Recently Viewed and this section contain the same product.
      const prefix = 'intent-' + container.dataset.sectionId;
      cards.forEach((slide, index) => {
        if (!slide) return;
        const ids = new Map();
        slide.querySelectorAll('[id]').forEach(element => {
          const old = element.id;
          const next = `${prefix}-${index}-${old}`;
          ids.set(old, next);
          element.id = next;
        });
        slide.querySelectorAll('*').forEach(element => {
          ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns'].forEach(attr => {
            const value = element.getAttribute(attr);
            if (value) element.setAttribute(attr, value.split(/\s+/).map(id => ids.get(id) || id).join(' '));
          });
          const href = element.getAttribute('href');
          if (href?.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href', '#' + ids.get(href.slice(1)));
        });
      });
      const validCards = cards.filter(Boolean);
      wrapper.replaceChildren(...validCards);
      const section = container.closest('.js-section');
      if (validCards.length) {
        section?.classList.remove('hidden');
        container.initSlider();
      } else {
        section?.classList.add('hidden');
        console.warn('[Intent recommendations] No native cards returned. Check products/<handle>?view=card.');
      }
      container.dataset.loaded = 'true';
    } catch (error) { console.warn('[Intent recommendations]', error); }
    finally { delete container.dataset.loading; }
  }
  function initialize(scope = document) {
    if (scope instanceof Element && scope.matches(selector)) load(scope);
    scope.querySelectorAll(selector).forEach(load);
  }
  async function start() {
    // products-feed loads Sahara's base slider and card scripts.
    await Promise.all([
      customElements.whenDefined('card-product-slider'),
      customElements.whenDefined('card-product')
    ]);
    if (!customElements.get('intent-recommendations-slider')) {
      const Base = customElements.get('card-product-slider');
      customElements.define('intent-recommendations-slider', class extends Base {});
    }
    initialize();
    document.addEventListener('shopify:section:load', event => initialize(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();